import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import {
  ArrowLeft, Loader2, ExternalLink, AlertCircle, Lightbulb, Store, RefreshCw, Clock, ListChecks, CheckCircle2,
} from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Skeleton } from '../../components/ui/Skeleton'
import { supabase } from '../../lib/supabaseClient'
import { useImmigrationCountries } from '../../hooks/useImmigration'
import { useImmigrationMilestones } from '../../hooks/useImmigrationTracker'
import { useAuth } from '../../contexts/AuthContext'
import { hasOwnerAccess } from '../../lib/owner'
import { cn } from '../../lib/utils'

// Per-step deep-dive page. The first time a step is opened we call
// the immigration-step-detail edge function to generate + cache the
// content (markdown walkthrough, vendors, pitfalls, tips). Subsequent
// visits read from the cache instantly.

export function ImmigrationStepPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { slug, stepNum } = useParams()
  const { user } = useAuth()
  const isAr = i18n.language === 'ar'

  const { countries, loading: countriesLoading } = useImmigrationCountries()
  const milestones = useImmigrationMilestones(slug)
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => {
    if (!user) return
    if (!hasOwnerAccess(user)) navigate('/', { replace: true })
  }, [user, navigate])

  const country = useMemo(
    () => countries.find((c) => c.slug === slug),
    [countries, slug],
  )

  const step = useMemo(() => {
    if (!country?.pathway_steps) return null
    return country.pathway_steps.find((s) => String(s.step) === String(stepNum))
  }, [country, stepNum])

  const fetchDetail = useCallback(async (force = false) => {
    if (!slug || !stepNum) return
    setLoading(!detail)
    setRefreshing(!!detail)
    setError(null)
    try {
      const { data, error: err } = await supabase.functions.invoke('immigration-step-detail', {
        body: {
          country_slug: slug,
          step_key: String(stepNum),
          language: isAr ? 'ar' : 'en',
          force,
        },
      })
      if (err) throw err
      if (data?.error) throw new Error(data.error)
      setDetail(data)
    } catch (e) {
      setError(e?.message || 'failed')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [slug, stepNum, isAr, detail])

  useEffect(() => {
    fetchDetail(false)
  }, [fetchDetail])

  if (countriesLoading) {
    return <div className="p-4"><Skeleton className="h-32 w-full" /></div>
  }

  if (!country || !step) {
    return (
      <div className="p-4 space-y-3">
        <button
          type="button"
          onClick={() => navigate(`/immigration/${slug || ''}`)}
          className="text-sm text-app-primary-500 hover:underline"
        >
          ← {t('immigration.title')}
        </button>
        <p className="text-sm text-muted-foreground" dir="auto">
          {t('immigration.notFound')}
        </p>
      </div>
    )
  }

  const countryName = isAr ? country.name_ar : country.name_en
  const stepTitle = isAr ? step.title_ar : step.title_en
  const stepWhat = isAr ? step.what_ar : step.what_en
  const stepDuration = isAr ? step.duration_ar : step.duration_en

  const alreadyTracked = milestones.items.some((m) => m.title === stepTitle)

  const trackStep = async () => {
    await milestones.create({
      country_slug: slug,
      title: stepTitle,
      description: stepWhat,
      status: 'planned',
      display_order: step.step,
    })
  }

  return (
    <div className="space-y-4 content-fade pb-8">
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={() => navigate(`/immigration/${slug}`)}
          aria-label={t('settings.back')}
          className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
        >
          <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] text-muted-foreground" dir="auto">
            {country.flag_emoji} {countryName} · {t('immigration.pathway.title')} ·
            {' '}
            {t('immigration.step.shortLabel', { n: step.step })}
          </p>
          <h1 className="text-lg font-bold text-foreground leading-tight" dir="auto">
            {stepTitle}
          </h1>
        </div>
        <button
          type="button"
          onClick={() => fetchDetail(true)}
          disabled={refreshing || loading}
          aria-label={t('immigration.step.refresh')}
          title={t('immigration.step.refresh')}
          className="p-2 rounded-md text-muted-foreground hover:bg-accent disabled:opacity-50"
        >
          <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
        </button>
      </div>

      {/* Step quick-facts strip */}
      <Card>
        <CardContent className="p-3 space-y-2.5">
          <p className="text-sm text-foreground leading-relaxed" dir="auto">{stepWhat}</p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px]">
            {stepDuration && (
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <Clock className="w-3 h-3" />
                {stepDuration}
              </span>
            )}
            {step.cost_sar_approx > 0 && (
              <span className="font-mono text-amber-700 dark:text-amber-400">
                ~{step.cost_sar_approx.toLocaleString('en-US')} SAR
              </span>
            )}
            <button
              type="button"
              onClick={trackStep}
              disabled={alreadyTracked}
              className={cn(
                'inline-flex items-center gap-1 px-2.5 py-1 rounded-md font-medium ms-auto',
                alreadyTracked
                  ? 'text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 cursor-default'
                  : 'text-app-primary-500 hover:bg-app-primary-500/10',
              )}
            >
              {alreadyTracked ? <CheckCircle2 className="w-3.5 h-3.5" /> : <ListChecks className="w-3.5 h-3.5" />}
              {alreadyTracked ? t('immigration.pathway.alreadyTracking') : t('immigration.pathway.trackThis')}
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Generated detail */}
      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-32 w-full rounded-lg" />
          <p className="text-xs text-muted-foreground text-center" dir="auto">
            {t('immigration.step.generating')}
          </p>
        </div>
      ) : error ? (
        <Card>
          <CardContent className="p-3 text-sm text-red-600 flex items-start gap-2" dir="auto">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <p>{error}</p>
              <button
                type="button"
                onClick={() => fetchDetail(false)}
                className="mt-1 text-xs text-app-primary-500 hover:underline"
              >
                {t('common.retry') || 'Retry'}
              </button>
            </div>
          </CardContent>
        </Card>
      ) : detail ? (
        <>
          {detail.detailed_guide_md && (
            <Card>
              <CardContent className="p-4 prose prose-sm dark:prose-invert max-w-none" dir="auto">
                <ReactMarkdown>{detail.detailed_guide_md}</ReactMarkdown>
              </CardContent>
            </Card>
          )}

          {(detail.vendors || []).length > 0 && (
            <Section icon={Store} title={t('immigration.step.vendors')}>
              <div className="space-y-2">
                {detail.vendors.map((v, i) => (
                  <div key={i} className="rounded-lg border border-input p-3" dir="auto">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-foreground">{v.name}</p>
                      {v.region && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                          {v.region}
                        </span>
                      )}
                    </div>
                    {v.type && <p className="text-[11px] text-muted-foreground">{v.type}</p>}
                    {v.notes && <p className="text-xs mt-1.5">{v.notes}</p>}
                    {v.url && (
                      <a
                        href={v.url}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-app-primary-500 hover:underline"
                        dir="ltr"
                      >
                        <ExternalLink className="w-3 h-3" />
                        {v.url.replace(/^https?:\/\//, '').slice(0, 50)}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {(detail.pitfalls || []).length > 0 && (
            <Section icon={AlertCircle} title={t('immigration.step.pitfalls')} tone="warn">
              <ul className="space-y-2">
                {detail.pitfalls.map((p, i) => (
                  <li key={i} className="text-sm" dir="auto">
                    <p className="font-semibold text-amber-700 dark:text-amber-400">{p.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{p.description}</p>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {(detail.tips || []).length > 0 && (
            <Section icon={Lightbulb} title={t('immigration.step.tips')} tone="good">
              <ul className="space-y-2">
                {detail.tips.map((p, i) => (
                  <li key={i} className="text-sm" dir="auto">
                    <p className="font-semibold text-emerald-700 dark:text-emerald-400">{p.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{p.body}</p>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {detail.generated_at && (
            <p className="text-[10px] text-muted-foreground/70 text-center" dir="auto">
              {t('immigration.step.generatedOn', { date: detail.generated_at.slice(0, 10) })}
              {detail.cached === false && ` · ${t('immigration.step.fresh')}`}
            </p>
          )}
        </>
      ) : null}
    </div>
  )
}

function Section({ icon: Icon, title, tone = 'neutral', children }) {
  return (
    <Card>
      <CardContent className="p-3 sm:p-4 space-y-2.5">
        <div className="flex items-center gap-2">
          <Icon className={cn(
            'w-4 h-4 flex-shrink-0',
            tone === 'warn' ? 'text-amber-600' : tone === 'good' ? 'text-emerald-600' : 'text-app-primary-500',
          )} />
          <h2 className="text-sm font-semibold text-foreground" dir="auto">{title}</h2>
        </div>
        {children}
      </CardContent>
    </Card>
  )
}
